import React, { useState } from "react";
import { RevoltModal } from "../../../components/RevoltModal";
import { RevoltTable, RevoltTableCell } from "../../../components";
import { RevoltSwitch } from "../../../components/RevoltSwitch";
import DropdownSelect from "../../../components/DropdownSelect";
import { ModelCapabilities } from "../../../components/ModelCapabilities";
import "./ModelsModal.css";

export function ModelsModal({ isOpen, onClose, user, onSelectModel }) {
  // Search state
  const [searchText, setSearchText] = useState("");

  // Filter state
  const [capabilityFilters, setCapabilityFilters] = useState({});

  // Sorting state
  const [sortBy, setSortBy] = useState("favorite"); // 'none', 'context', 'freeTier', 'name', 'provider', 'favorite', 'priceInput', 'priceOutput', 'priceBoth'
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc', 'desc'

  // Favorite state (modelId -> boolean)
  const [favoritedModels, setFavoritedModels] = useState({});

  // Loading state for favorite updates (modelId -> boolean)
  const [updatingFavorites, setUpdatingFavorites] = useState({});

  // Free models are always excluded

  const isSelectMode = Boolean(onSelectModel);

  // Fetch user model settings (including favorites) - skip in select mode
  const fetchUserModelSettings = React.useCallback(async () => {
    try {
      const response = await fetch("/api/account/models", {
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Extract favorite status from model settings
        const favorites = {};
        Object.entries(data.models || {}).forEach(([modelId, modelData]) => {
          if (modelData && typeof modelData.favorite === "boolean") {
            favorites[modelId] = modelData.favorite;
          }
        });
        setFavoritedModels(favorites);
      }
    } catch (error) {
      console.error("Failed to fetch user model settings:", error);
      // Keep existing state on error
    }
  }, []);

  // Update model favorite status
  const updateModelFavorite = React.useCallback(async (modelId, isFavorite) => {
    // Set loading state for this specific model
    setUpdatingFavorites((prev) => ({ ...prev, [modelId]: true }));

    try {
      const response = await fetch(
        `/api/account/models/${encodeURIComponent(modelId)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            data: { favorite: isFavorite },
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Failed to update favorite");
      }

      // Success - keep the optimistic update
      setFavoritedModels((prev) => ({
        ...prev,
        [modelId]: isFavorite,
      }));
    } catch (error) {
      console.error("Failed to update model favorite:", error);
      // Revert optimistic update on failure
      setFavoritedModels((prev) => ({
        ...prev,
        [modelId]: !isFavorite, // Revert to opposite of what we tried to set
      }));
      // Could show user error message here
      throw error;
    } finally {
      // Clear loading state regardless of success/failure
      setUpdatingFavorites((prev) => ({ ...prev, [modelId]: false }));
    }
  }, []);

  // Models data state
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and sort models based on search, filter, and sorting criteria
  const getFilteredModels = React.useCallback(() => {
    let filtered = models.filter((model) => {
      // Exclude special OpenRouter routing models
      const modelId = model.id || "";
      const modelName = (model.name || "").toLowerCase();
      if (
        modelId.includes("openrouter/auto") ||
        modelId.includes("openrouter/bodybuilder") ||
        modelName.includes("bodybuilder") ||
        modelName.includes("auto router")
      ) {
        return false;
      }

      // Text search filter (name and provider)
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();
        const name = (model.name || model.id || "").toLowerCase();
        const provider = (model.provider || "").toLowerCase();

        if (!name.includes(searchLower) && !provider.includes(searchLower)) {
          return false;
        }
      }

      // Capabilities filter
      const enabledCapabilityFilters = Object.entries(capabilityFilters).filter(
        ([_, config]) => config.enabled
      );
      if (enabledCapabilityFilters.length > 0) {
        for (const [capability, config] of enabledCapabilityFilters) {
          const modelCapabilities = model.detectedCapabilities || [];
          const hasCapability = modelCapabilities.some((cap) =>
            cap.toLowerCase().includes(capability.toLowerCase())
          );

          if (config.mode === "include" && !hasCapability) {
            return false; // Model must have this capability but doesn't
          }
          if (config.mode === "exclude" && hasCapability) {
            return false; // Model must NOT have this capability but does
          }
        }
      }

      // Always exclude free models
      if (model.isFreeTier) {
        return false;
      }

      return true;
    });

    // Apply sorting
    if (sortBy !== "none") {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = (a.name || a.id || "").toLowerCase();
            bValue = (b.name || b.id || "").toLowerCase();
            break;
          case "provider":
            aValue = (a.provider || "").toLowerCase();
            bValue = (b.provider || "").toLowerCase();
            break;
          case "context":
            aValue = a.context_length || 0;
            bValue = b.context_length || 0;
            break;
          case "freeTier":
            aValue = a.isFreeTier ? 1 : 0; // Free = 1, Paid = 0
            bValue = b.isFreeTier ? 1 : 0;
            break;
          case "favorite":
            aValue = favoritedModels[a.id] ? 1 : 0; // Favorite = 1, Not favorite = 0
            bValue = favoritedModels[b.id] ? 1 : 0;
            break;
          case "priceInput":
            aValue = a.pricingNumeric?.prompt || 0;
            bValue = b.pricingNumeric?.prompt || 0;
            break;
          case "priceOutput":
            aValue = a.pricingNumeric?.completion || 0;
            bValue = b.pricingNumeric?.completion || 0;
            break;
          case "priceBoth":
            // Average of input and output prices
            aValue =
              ((a.pricingNumeric?.prompt || 0) +
                (a.pricingNumeric?.completion || 0)) /
              2;
            bValue =
              ((b.pricingNumeric?.prompt || 0) +
                (b.pricingNumeric?.completion || 0)) /
              2;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [
    models,
    searchText,
    capabilityFilters,
    sortBy,
    sortDirection,
    favoritedModels,
  ]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filteredModels = getFilteredModels();
  const totalPages = Math.ceil(filteredModels.length / pageSize);

  // Fetch models data
  const fetchModels = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/openrouter/models", {
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || `HTTP error ${response.status}`);
      }

      if (data.success) {
        setModels(data.models || []);
      } else {
        throw new Error(data?.error || "Failed to fetch models");
      }
    } catch (err) {
      setError(err.message || "Failed to load models");
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      fetchModels();
      if (!isSelectMode) {
        fetchUserModelSettings();
      }
    }
  }, [isOpen, fetchModels, fetchUserModelSettings, isSelectMode]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchText, capabilityFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(Number(size));
    setCurrentPage(1);
  };

  const handleToggleFavorite = async (modelId) => {
    // Prevent multiple clicks while updating
    if (updatingFavorites[modelId]) {
      return;
    }

    const currentFavorite = favoritedModels[modelId] || false;
    const newFavorite = !currentFavorite;

    // Optimistic update - immediately update UI
    setFavoritedModels((prev) => ({
      ...prev,
      [modelId]: newFavorite,
    }));

    try {
      await updateModelFavorite(modelId, newFavorite);
      // Success - optimistic update is kept
    } catch (error) {
      // Error handled in updateModelFavorite (reverts optimistic update)
    }
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span>
          <i className="fa-solid fa-brain"></i> AI Models
        </span>
      }
      containerClassName="modal-wide models-modal-container"
    >
      <div className="models-modal-content">
        <div className="models-content-layout">
          {/* Sidebar - Model Capabilities */}
          <div className="models-sidebar">
            <div className="sidebar-header">
              <h4>Model Filters</h4>
              <button
                className="clear-filters-btn"
                onClick={() => setCapabilityFilters({})}
              >
                Clear All
              </button>
            </div>
            <ModelCapabilities
              capabilityFilters={capabilityFilters}
              onCapabilityChange={setCapabilityFilters}
            />
          </div>

          {/* Main Content Area */}
          <div className="models-main-content">
            {/* Combined Search and Sorting Controls */}
            <div className="models-controls">
              <div className="controls-row">
                <div className="search-input-group">
                  <input
                    id="model-search-modal"
                    type="text"
                    placeholder="Search models..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="models-search-input"
                  />
                </div>

                <div className="sort-by-group">
                  <DropdownSelect
                    value={sortBy}
                    onValueChange={(value) => {
                      setSortBy(value);
                      // Set default direction based on sort type
                      // Favorite should be descending (favorites first), others ascending
                      setSortDirection(value === "favorite" ? "desc" : "asc");
                    }}
                    options={[
                      { value: "none", label: "No Sorting" },
                      { value: "name", label: "Name" },
                      { value: "provider", label: "Provider" },
                      { value: "context", label: "Context Length" },
                      { value: "freeTier", label: "Free Tier" },
                      { value: "favorite", label: "Favorite" },
                      { value: "priceInput", label: "Input Price" },
                      { value: "priceOutput", label: "Output Price" },
                      { value: "priceBoth", label: "Avg Price" },
                    ]}
                    placeholder="Sort by..."
                    className="sort-select"
                  />
                </div>

                {sortBy !== "none" && (
                  <div className="sort-direction-group">
                    <button
                      className={`direction-toggle ${sortDirection === "asc" ? "active" : ""}`}
                      onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                      title={sortDirection === "asc" ? "Ascending" : "Descending"}
                    >
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </button>
                  </div>
                )}
              </div>
            </div>


            {/* Models Table */}
            <div className="models-table-container">
              {loading ? (
                <div className="models-loading">Loading models...</div>
              ) : error ? (
                <div className="models-error">
                  <p>Failed to load models: {error}</p>
                  <button onClick={() => window.location.reload()}>
                    Retry
                  </button>
                </div>
              ) : (
                <RevoltTable
                  className="scrollable"
                  headers={[
                    "Provider",
                    "Name",
                    "Capabilities",
                    "Cxt Length",
                    "Input $/M",
                    "Output $/M",
                    isSelectMode ? "Select" : "Favorite",
                  ]}
                  gridColumns={[0.5, 1, 0.3, 0.5, 0.3, 0.35, 0.25]}
                  emptyMessage="No models found matching your criteria."
                  rows={getFilteredModels()
                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                    .map((model) => [
                      <span
                        className={`model-provider-cell ${model.provider?.toLowerCase() || "unknown"}`}
                        title={model.provider || "Unknown"}
                      >
                        {model.provider || "Unknown"}
                      </span>,
                      <span
                        className="model-name-cell"
                        title={model.name || model.id}
                      >
                        {model.name || model.id}
                      </span>,
                      <span
                        className="model-capabilities-cell"
                        title={model.detectedCapabilities?.join(", ") || ""}
                      >
                        {model.detectedCapabilities &&
                        model.detectedCapabilities.length > 0
                          ? model.detectedCapabilities.slice(0, 2).join(", ") +
                            (model.detectedCapabilities.length > 2
                              ? ` +${model.detectedCapabilities.length - 2} more`
                              : "")
                          : "None"}
                      </span>,
                      <span
                        className="model-context-cell"
                        title={
                          model.context_length
                            ? model.context_length.toString()
                            : "—"
                        }
                      >
                        {model.context_length
                          ? model.context_length.toLocaleString()
                          : "—"}
                      </span>,
                      <span
                        className="model-price-cell"
                        title={
                          model.pricingNumeric?.prompt
                            ? `$${(model.pricingNumeric.prompt * 1000000).toFixed(4)} per million tokens`
                            : "—"
                        }
                      >
                        {model.pricingNumeric?.prompt
                          ? `$${(model.pricingNumeric.prompt * 1000000).toFixed(2)}`
                          : "—"}
                      </span>,
                      <span
                        className="model-price-cell"
                        title={
                          model.pricingNumeric?.completion
                            ? `$${(model.pricingNumeric.completion * 1000000).toFixed(4)} per million tokens`
                            : "—"
                        }
                      >
                        {model.pricingNumeric?.completion
                          ? `$${(model.pricingNumeric.completion * 1000000).toFixed(2)}`
                          : "—"}
                      </span>,
                      isSelectMode ? (
                        <RevoltTableCell className="model-select-cell">
                          <button
                            className="model-select-btn"
                            onClick={() => {
                              onSelectModel(model);
                              onClose();
                            }}
                          >
                            Select
                          </button>
                        </RevoltTableCell>
                      ) : (
                        <RevoltTableCell className="model-favorite-cell">
                          <div className="favorite-switch-container">
                            <RevoltSwitch
                              checked={favoritedModels[model.id] || false}
                              onChange={() => handleToggleFavorite(model.id)}
                              disabled={updatingFavorites[model.id] || false}
                              className="favorite-switch"
                            />
                          </div>
                        </RevoltTableCell>
                      ),
                    ])}
                />
              )}
            </div>

            {/* Pagination Controls with Stats */}
            {!loading && !error && (
              <div className="models-pagination">
                <div className="page-size">
                  <span>Show</span>
                  <DropdownSelect
                    value={pageSize}
                    options={[10, 20, 50, 100]}
                    onValueChange={(val) => handlePageSizeChange(Number(val))}
                  />
                  <span>per page</span>
                </div>

                {/* Models Stats - moved into pagination center */}
                <div className="models-stats">
                  <span className="models-count">
                    {filteredModels.length} of {models.length} model
                    {models.length !== 1 ? "s" : ""}
                    {(searchText.trim() ||
                      Object.keys(capabilityFilters).some(
                        (key) => capabilityFilters[key].enabled
                      )) &&
                      " (filtered)"}
                  </span>
                </div>

                {/* Page controls - only show if multiple pages */}
                {filteredModels.length > pageSize && (
                  <div className="page-controls">
                    <button
                      className="page-btn"
                      onClick={() =>
                        handlePageChange(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage <= 1}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <span className="page-indicator">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      className="page-btn"
                      onClick={() =>
                        handlePageChange(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage >= totalPages}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </RevoltModal>
  );
}
